import type {
  CoreEditorSlice,
  EditorImmerGetState,
  EditorImmerSetState,
} from './editor-store-types';
import { useVersioningStore } from '../versioning/versioning-store';
import {
  VersionIntegrationContext,
  VersioningIntegrationUtils,
  CollaborationVersioningUtils,
} from './versioning-integration';

export interface VersioningSlice {
  // Versioning integration state
  versioningEnabled: boolean;
  lastVersionSnapshot: Date | null;

  // Versioning integration
  enableVersioning: () => void;
  disableVersioning: () => void;
  isVersioningEnabled: () => boolean;
  createVersionSnapshot: (name?: string, description?: string) => Promise<void>;
  syncWithVersioningStore: () => void;
  withoutVersionTracking: <T>(action: () => T) => T;
}

export const versioningInitialState: Pick<
  VersioningSlice,
  'versioningEnabled' | 'lastVersionSnapshot'
> = {
  // Versioning integration initial state
  versioningEnabled: true,
  lastVersionSnapshot: null,
};

function syncVersioningProjectState(
  project: { id: string; currentVersion: number },
): void {
  useVersioningStore.setState((state) => ({
    ...state,
    projectId: project.id,
    currentVersion:
      state.currentVersion === null ? project.currentVersion : state.currentVersion,
    selectedVersion:
      state.selectedVersion === null ? project.currentVersion : state.selectedVersion,
  }));
}

export function createVersioningSlice<
  TState extends VersioningSlice & Pick<CoreEditorSlice, 'project' | 'isModified'>,
>(
  set: EditorImmerSetState<TState>,
  get: EditorImmerGetState<TState>,
): VersioningSlice {
  return {
    ...versioningInitialState,

    // Versioning integration actions
    enableVersioning: () => {
      set((state) => {
        state.versioningEnabled = true;
      });
      VersionIntegrationContext.getInstance().enableVersioning();
    },

    disableVersioning: () => {
      set((state) => {
        state.versioningEnabled = false;
      });
      VersionIntegrationContext.getInstance().disableVersioning();
    },

    isVersioningEnabled: () => {
      return get().versioningEnabled;
    },

    createVersionSnapshot: async (name?: string, description?: string) => {
      const state = get();
      if (!state.project) return;

      try {
        syncVersioningProjectState(state.project);
        await VersioningIntegrationUtils.createSnapshot(name, description);

        // Always update the editor state to mark snapshot creation
        set((editorState) => {
          editorState.lastVersionSnapshot = new Date();
          editorState.isModified = false;
        });
      } catch (error) {
        console.error('Failed to create version snapshot:', error);
        // Don't throw - maintain backward compatibility
      }
    },

    syncWithVersioningStore: () => {
      const state = get();
      if (!state.project) return;

      // Sync with existing versioning system via bridge
      try {
        syncVersioningProjectState(state.project);
      } catch (error) {
        console.warn('Versioning sync failed:', error);
      }
    },

    withoutVersionTracking: <T>(action: () => T): T => {
      return VersioningIntegrationUtils.withoutVersioning(action);
    },
  };
}

export { CollaborationVersioningUtils };
